import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import Card from '../../components/Card';
import Button from '../../components/Button';
import { 
  User, 
  Mail, 
  Phone, 
  Building, 
  Shield,
  ArrowLeft,
  Save,
  UserPlus
} from 'lucide-react';
import { useForm } from 'react-hook-form';
import DepartmentCheckboxes from '../../components/DepartmentCheckboxes';
import toast from 'react-hot-toast';
import apiService from '../../services/apiService';
import axios from 'axios';

interface UserForm {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  role: 'admin' | 'technician' | 'client';
  company: string;
  address: {
    street: string;
    city: string;
    postalCode: string;
    country: string;
  };
  departements: string[];  
  permissions: string[];
  isActive: boolean;
}

const EditUser: React.FC = () => {
  const params = useParams<{ id: string }>();
  const userId = (params as any)?.id || (params as any)?.userId;
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const { register, handleSubmit, watch, formState: { errors }, reset, setValue } = useForm<UserForm>();

  useEffect(() => {
    const fetchUser = async () => {
      if (!userId) return;
      try {
        const response = await apiService.users.getById(userId);
        const userData =
          response?.data?.data?.user ??
          response?.data?.user ??
          response?.data?.data ??
          response?.data;

        if (userData) {
          reset({
            ...userData,
            departements: Array.isArray(userData.departements)
              ? userData.departements
              : (userData.departement ? [userData.departement] : []),
          });
        }
      } catch (error) {
        console.error("Failed to fetch user data", error);
        toast.error("Erreur lors de la récupération de l'utilisateur.");
      }
    };
    fetchUser();
  }, [userId, reset]);

  const selectedRole = watch('role') || 'client';

  const rolePermissions = {
    admin: [
      'users.manage',
      'companies.manage',
      'requests.manage',
      'quotes.manage',
      'payments.manage',
      'reports.manage',
      'messages.manage'
    ],
    technician: [
      'requests.read',
      'requests.update',
      'quotes.read',
      'installations.manage',
      'reports.create',
      'messages.manage'
    ],
    client: [
      'requests.create',
      'requests.read',
      'quotes.read',
      'quotes.respond',
      'installations.read',
      'payments.read',
      'messages.create'
    ]
  };

  const onSubmit = async (data: UserForm) => {
    if (!userId) return;
    setIsLoading(true);
    try {
      const userDataToSend = {
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        phone: data.phone,
        role: data.role,
        company: data.company,
        address: data.address,
        departements: data.departements,
        isActive: data.isActive
      };

      await apiService.users.update(userId, userDataToSend);

      toast.success('Utilisateur mis à jour — la page reste ici 👍');
      // ➜ On ne navigue pas : la page ne “saute” pas.
      // Si tu veux recharger les données sans naviguer :
      // const fresh = await apiService.users.getById(userId);
      // reset({ ...fresh.data.data.user });

    } catch (error: unknown) {
      console.error('Erreur détaillée:', error);
      if (axios.isAxiosError(error)) {
        toast.error(error.response?.data?.message || 'Erreur lors de la mise à jour');
      } else {
        toast.error('Une erreur inconnue est survenue lors de la mise à jour.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-4xl mx-auto space-y-8 pb-24">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Button variant="ghost" onClick={() => navigate('/admin/users')}> 
              <ArrowLeft className="w-5 h-5 mr-2" />
              Retour
            </Button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center">
                <UserPlus className="w-8 h-8 mr-3 text-primary-600" />
                Modifier l'Utilisateur
              </h1>
              <p className="text-gray-600 mt-2">
                Mettre à jour un compte utilisateur existant
              </p>
            </div>
          </div>
        </div>

        <form
          onSubmit={handleSubmit(onSubmit)}
          className="space-y-8"
          onKeyDown={(e) => { if (e.key === 'Enter' && (e.target as HTMLElement).tagName !== 'TEXTAREA') e.preventDefault(); }}
          autoComplete="off"
        >
          {/* Informations personnelles */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
              <User className="w-5 h-5 mr-2 text-primary-600" />
              Informations Personnelles
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Prénom *
                </label>
                <input
                  type="text"
                  {...register('firstName', { required: 'Le prénom est requis' })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Jean"
                  autoComplete="given-name"
                />
                {errors.firstName && (
                  <p className="mt-1 text-sm text-red-600">{errors.firstName.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nom *
                </label>
                <input
                  type="text"
                  {...register('lastName', { required: 'Le nom est requis' })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Dupont"
                  autoComplete="family-name"
                />
                {errors.lastName && (
                  <p className="mt-1 text-sm text-red-600">{errors.lastName.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Mail className="w-4 h-4 inline mr-1" />
                  Email *
                </label>
                <input
                  type="email"
                  {...register('email', {
                    required: 'L\'email est requis',
                    pattern: {
                      value: /^\S+@\S+$/i,
                      message: 'Email invalide'
                    }
                  })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="jean.dupont@email.com"
                  autoComplete="email"
                />
                {errors.email && (
                  <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Phone className="w-4 h-4 inline mr-1" />
                  Téléphone
                </label>
                <input
                  type="tel"
                  {...register('phone')}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="06 12 34 56 78"
                  autoComplete="tel-national"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Building className="w-4 h-4 inline mr-1" />
                  Entreprise
                </label>
                <input
                  type="text"
                  {...register('company')}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Nom de l'entreprise"
                  autoComplete="organization"
                />
              </div>
            </div>
          </Card>

          {/* Rôle et permissions */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
              <Shield className="w-5 h-5 mr-2 text-primary-600" />
              Rôle et Permissions
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Rôle *
                </label>
                <select
                  {...register('role', { required: 'Le rôle est requis' })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="client">Client</option>
                  <option value="technician">Technicien</option>
                  <option value="admin">Administrateur</option>
                </select>
                {errors.role && (
                  <p className="mt-1 text-sm text-red-600">{errors.role.message}</p>
                )}
              </div>

              {['technician', 'client'].includes(selectedRole) && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Départements *
                  </label>
                  <DepartmentCheckboxes
                    value={watch('departements') || []}
                    onChange={(vals) => setValue('departements', vals, { shouldValidate: true })}
                    required
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Statut
                </label>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    {...register('isActive')}
                    className="mr-2 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm text-gray-700">Compte actif</span>
                </div>
              </div>
            </div>

            {/* Permissions preview */}
            <div className="mt-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Permissions accordées
              </label>
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {rolePermissions[selectedRole as keyof typeof rolePermissions].map((permission) => (
                    <div key={permission} className="flex items-center text-sm text-gray-600">
                      <div className="w-2 h-2 bg-primary-500 rounded-full mr-2"></div>
                      {permission.replace('.', ' - ')}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </Card>

          {/* Actions */}
          <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 shadow-lg z-10">
            <div className="max-w-4xl mx-auto flex justify-end space-x-4">
              <Button
                type="button"
                variant="ghost"
                onClick={() => navigate('/admin/users')}
              >
                Annuler
              </Button>
              <Button
                type="submit"
                isLoading={isLoading}
                size="lg"
              >
                <Save className="w-4 h-4 mr-2" />
                Enregistrer les modifications
              </Button>
            </div>
          </div>
        </form>
      </div>
    </Layout>
  );
};

export default EditUser;
